import React, { useEffect, useState } from 'react';
import { supabase } from '../../../utils/supabase';
import { Panel } from '../primitives';
import { shortDate } from './dates';
import { UtPostMatchReportLoader } from './UtPostMatchReport';
import { InjuryClaimLink } from './InjuryClaimLink';

interface LastGame {
  id: string;
  sequence_number: number;
  date: string;
  score_blue: number | null;
  score_orange: number | null;
}

/** Most recent completed game. */
function useLastResult() {
  const [game, setGame] = useState<LastGame | null>(null);
  useEffect(() => {
    let alive = true;
    supabase
      .from('games')
      .select('id, sequence_number, date, score_blue, score_orange')
      .eq('completed', true)
      .order('sequence_number', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (alive) setGame((data as LastGame | null) ?? null);
      });
    return () => {
      alive = false;
    };
  }, []);
  return game;
}

/**
 * Last result strip for the game page: score, the post-match report sheet and,
 * for players in it, the injury claim inside the claim window.
 */
export const LastResult: React.FC<{ playerId: string | null }> = ({ playerId }) => {
  const game = useLastResult();
  const [report, setReport] = useState(false);
  if (!game) return null;

  return (
    <Panel pad="sm" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ut-mut">
            Last game · #{game.sequence_number} · {shortDate(game.date)}
          </span>
          <span className="font-ut-display text-xl font-extrabold">
            <span className="text-ut-blue">BLUE</span> {game.score_blue ?? '–'} – {game.score_orange ?? '–'}{' '}
            <span className="text-ut-orange">ORANGE</span>
          </span>
        </div>
        <button
          type="button"
          onClick={() => setReport(true)}
          className="flex-none rounded-lg border border-ut-line px-3 py-2 text-[13px] font-semibold hover:bg-ut-card2"
        >
          Report
        </button>
      </div>
      <InjuryClaimLink playerId={playerId} gameId={game.id} matchNumber={game.sequence_number} gameDate={game.date} />
      <UtPostMatchReportLoader open={report} onClose={() => setReport(false)} game={game} />
    </Panel>
  );
};
